import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TextOverlay } from 'blocks/TextOverlayModal';
import { TextOverlayV2 } from 'blocks/TextOverlayModal/v2';
import { useTextOverlaySwitch } from 'blocks/TextOverlaySwitchModal';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import {
  clearTrackForStyling,
  selectTrackForStyling,
} from 'redux/modules/embed/actions';
import { globalStyleTrackIdSelector } from 'redux/modules/embed/selectors';
import { pushModal } from 'redux/modules/modal';
import { subtierSelector } from 'redux/modules/pricing';
import { isEnterpriseSubTier } from 'utils/pricing';

type ActiveModal = 'legacy' | 'current' | 'none';

interface UseGlobalTextModalSwitcher {
  activeModal: ActiveModal;
  onExited: () => void;
  onHide: () => void;
  onToggleLegacyMode: (partialOverlay?: TextOverlay | TextOverlayV2) => void;
  transitionOverlay: TextOverlay | TextOverlayV2;
}

const useGlobalTextModalSwitcher = (): UseGlobalTextModalSwitcher => {
  const dispatch = useDispatch();

  const [hasPickedMode, setHasPickedMode] = React.useState(false);

  const trackId = useSelector(globalStyleTrackIdSelector);
  const { onHide, onExited, show } = useConnectedModal('GlobalStyleModal');

  const handleHide = React.useCallback(() => {
    dispatch(clearTrackForStyling());
    onHide();
  }, [dispatch, onHide]);

  // Enterprise users should have all the text boxes modals
  // defaulted to the the legacy editor by default.
  // However as the new modal has no constraints, the new text
  // overlay modal should keep track when the user has picked to
  // use the new modal.
  const userSubtier = useSelector(subtierSelector);
  const isEnterpriseUser = isEnterpriseSubTier(userSubtier);
  const shouldForceLegacyMode = show && isEnterpriseUser && !hasPickedMode;
  const startingModalType = shouldForceLegacyMode ? 'legacy' : undefined;

  const handleOpen = React.useCallback(() => {
    dispatch(selectTrackForStyling(trackId));
    dispatch(pushModal({ name: 'GlobalStyleModal' }));
  }, [dispatch, trackId]);

  const handleSwitchMode = React.useCallback(() => {
    setHasPickedMode(true);
  }, []);

  const {
    activeModal,
    onToggleLegacyMode,
    transitionOverlay,
  } = useTextOverlaySwitch({
    onHide: handleHide,
    onOpen: handleOpen,
    onSwitchMode: handleSwitchMode,
    show,
    startingModalType,
  });

  return {
    activeModal,
    onExited,
    onHide: handleHide,
    onToggleLegacyMode,
    transitionOverlay,
  };
};

export default useGlobalTextModalSwitcher;
